import { Action, createReducer, on } from '@ngrx/store';
import { LoadStatus } from '../../load-status';
import {
    barcodeClipboardLoadedFromStorage,
    hydrateBarcodeClipboard,
    setBarcodeClipboardRequested,
} from '../actions/barcode-clipboard.actions';
import {
    BarcodeClipboardState,
    capBarcodeQueue,
    initialBarcodeClipboardState,
    PaceBarcode,
} from '../barcode-clipboard-collection.state';

/**
 * Replaces the clipboard, applying the retention cap. Routed from both an explicit set and a
 * storage hydration - a hydrate result is just as authoritative a replacement as an explicit
 * write, so both settle into `Loaded`.
 *
 * @param state the barcode clipboard slice
 * @param props the action payload
 * @param props.barcodes the candidate clipboard contents (uncapped)
 * @returns the slice with the capped clipboard and `Loaded` status
 */
export function setBarcodesReducer(
    state: Readonly<BarcodeClipboardState>,
    props: { barcodes: PaceBarcode[] },
): Readonly<BarcodeClipboardState> {
    return {
        ...state,
        barcodes: capBarcodeQueue(props.barcodes),
        status: LoadStatus.Loaded,
    };
}

/**
 * Marks the slice as loading while the fetch effect (re)hydrates it from storage.
 *
 * @param state the barcode clipboard slice
 * @returns the slice with `Loading` status
 */
export function setBarcodeLoadingReducer(state: Readonly<BarcodeClipboardState>): Readonly<BarcodeClipboardState> {
    return { ...state, status: LoadStatus.Loading };
}

// Persist failures (`barcodeClipboardStorageError`) deliberately have no handler here: flipping
// `status` to `Error` would falsely invalidate a clipboard that is still correct in the store. A
// failed write is logged and reported to the caller, and the clipboard stays as it is.
const createBarcodeClipboardReducer = createReducer(
    initialBarcodeClipboardState,
    on(setBarcodeClipboardRequested, setBarcodesReducer),
    on(barcodeClipboardLoadedFromStorage, setBarcodesReducer),
    on(hydrateBarcodeClipboard, setBarcodeLoadingReducer),
);

export function barcodeClipboardReducer(
    state: Readonly<BarcodeClipboardState> | undefined,
    action: Action,
): Readonly<BarcodeClipboardState> {
    return createBarcodeClipboardReducer(state, action);
}
